import { Checkbox, Dialog, Popover, Tooltip } from "@mui/material";
import BulkEditBlogCategory from "./BulkEditBlogCategory";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Edit,
  Filter,
  PlusCircle,
  Trash,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { fetchBlogCategories, deleteBlogCategories } from "../../services/blogCategoryService";
import { DataLoad, PlanPermission, TableColumnsContext } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { formatDate } from "../../controllers/FormatAmount";
import { parentNavigate } from "../../controllers/ParentNavigate";
import {
  ButtonComponent,
  CommonTable,
  ConfirmationPopup,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent
} from "@shopeasy-dashboard-ui/ui-library";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import { useUser } from "../../controllers/UserContext";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

const getInitialUrlParams = (location) => {
  const urlParams = new URLSearchParams(window.location.search);
  const searchParam =
    urlParams.get("s") ||
    urlParams.get("search") ||
    (location?.state?.searchkey ? location.state.searchkey : "");
  const pageParam = parseInt(urlParams.get("page"), 10);
  return { searchParam, pageParam };
};

const BlogCategories = () => {
  const handleTableApi = useTableApi();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { tableColumnsData } = useContext(TableColumnsContext);
  const userContext = useUser();
  const { setPlanPermission } = useContext(PlanPermission);
  const { dataLoading, setDataLoading } = useContext(DataLoad);

  const initialUrlParams = useMemo(() => getInitialUrlParams(location), []);

  const [Page, setPage] = useState(() => {
    if (initialUrlParams.pageParam && initialUrlParams.pageParam > 0) {
      return initialUrlParams.pageParam;
    }
    if (location?.state?.prevPage) return Number(location.state.prevPage);
    return 1;
  });

  const initialLimit = localStorage.getItem("limit");
  const [limit, setLimit] = useState(initialLimit || 10);
  const [search, setSearch] = useState(initialUrlParams.searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [BlogCategory, setAllBlogCategory] = useState();

  const [isNotification, setIsNotification] = useState(false);
  const [clearRow, setClearRow] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [selectedRowsId, setSelectedRowsId] = useState([]);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Treeview expand/collapse state
  const [collapsedIds, setCollapsedIds] = useState([]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Sync URL search params and notify parent shell
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    if (debouncedSearch && debouncedSearch.trim().length >= 3) {
      urlParams.set("search", debouncedSearch.trim());
    } else {
      urlParams.delete("search");
      urlParams.delete("s");
    }

    if (Page > 1) {
      urlParams.set("page", Page);
    } else {
      urlParams.delete("page");
    }

    const newQueryString = urlParams.toString();
    const newUrl = newQueryString
      ? `${window.location.pathname}?${newQueryString}`
      : window.location.pathname;

    if (window.location.search !== (newQueryString ? `?${newQueryString}` : "")) {
      window.history.replaceState({}, "", newUrl);
      parentNavigate(newUrl);
    }
  }, [Page, debouncedSearch]);

  // TanStack Query for Blog Categories
  const {
    data: categoryQueryData,
    isLoading: isCategoryQueryLoading,
    isFetching: isCategoryQueryFetching,
    error: categoryQueryError,
  } = useQuery({
    queryKey: ["blogCategories", Page, limit, debouncedSearch],
    queryFn: () =>
      fetchBlogCategories({
        page: Page,
        limit: limit === "all" ? 1000 : limit,
        searchkey: debouncedSearch,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (categoryQueryData) {
      setAllBlogCategory(categoryQueryData);
    }
  }, [categoryQueryData]);

  useEffect(() => {
    setDataLoading(isCategoryQueryLoading || isCategoryQueryFetching);
    return () => {
      setDataLoading(false);
    };
  }, [isCategoryQueryLoading, isCategoryQueryFetching, setDataLoading]);

  useEffect(() => {
    if (categoryQueryError) {
      if (categoryQueryError?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "Blog" });
      }
      setIsNotification(true);
      setNotificationMsg(categoryQueryError?.message || categoryQueryError);
      setSeverity("error");
    }
  }, [categoryQueryError, setPlanPermission]);

  useEffect(() => {
    localStorage.setItem("limit", limit);
  }, [limit]);

  const categoryTotalRecords = useMemo(() => {
    return BlogCategory?.data?.totalRecords || 0;
  }, [BlogCategory?.data?.totalRecords]);

  const categoryEffectiveLimit = useMemo(() => {
    return limit === "all" ? categoryTotalRecords : Number(limit || 10);
  }, [limit, categoryTotalRecords]);

  const categoryTotalPages = useMemo(() => {
    if (limit === "all") return 1;
    return Math.ceil(categoryTotalRecords / (Number(limit) || 10)) || 1;
  }, [categoryTotalRecords, limit]);

  const startRec = useMemo(() => {
    return BlogCategory?.data?.currentPage ? (BlogCategory.data.currentPage - 1) * categoryEffectiveLimit + 1 : 0;
  }, [BlogCategory?.data?.currentPage, categoryEffectiveLimit]);

  const endRec = useMemo(() => {
    return BlogCategory?.data?.currentPage ? Math.min(
      BlogCategory.data.currentPage * categoryEffectiveLimit,
      categoryTotalRecords
    ) : 0;
  }, [BlogCategory?.data?.currentPage, categoryEffectiveLimit, categoryTotalRecords]);

  const toggleCollapse = useCallback((id) => {
    setCollapsedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const processedRecords = useMemo(() => {
    const records = BlogCategory?.data?.records;
    if (!records || !Array.isArray(records)) return [];

    const parsed = records.map((item) => {
      let pathParts = [];
      if (typeof item?.parent_category === "string" && item.parent_category.trim()) {
        pathParts = item.parent_category.split(">").map((s) => s.trim()).filter(Boolean);
      } else if (item?.categoryName || item?.blogcategory_name) {
        pathParts = [item?.categoryName || item?.blogcategory_name];
      } else {
        pathParts = ["-"];
      }

      const level = Math.max(0, pathParts.length - 1);
      const displayName = item?.categoryName || item?.blogcategory_name || pathParts[pathParts.length - 1] || "-";
      const fullPath = pathParts.join(" > ");

      return {
        ...item,
        _treeLevel: level,
        _treeDisplayName: displayName,
        _treeFullPath: fullPath,
        _treePathParts: pathParts,
      };
    });

    return parsed.map((item) => {
      const hasChildren = parsed.some(
        (other) =>
          other._id !== item._id &&
          other._treeFullPath.startsWith(item._treeFullPath + " > ")
      );
      return {
        ...item,
        _hasChildren: hasChildren,
      };
    });
  }, [BlogCategory?.data?.records]);

  const isRowVisible = useCallback(
    (row) => {
      if (search && search.trim().length > 0) return true;
      if (!collapsedIds.length) return true;
      if (!row._treePathParts || row._treePathParts.length <= 1) return true;

      for (let i = 1; i < row._treePathParts.length; i++) {
        const ancestorPath = row._treePathParts.slice(0, i).join(" > ");
        const ancestorRow = processedRecords.find((r) => r._treeFullPath === ancestorPath);
        if (ancestorRow && collapsedIds.includes(ancestorRow._id)) {
          return false;
        }
      }
      return true;
    },
    [collapsedIds, processedRecords, search]
  );

  const visibleRecords = useMemo(() => {
    return processedRecords.filter(isRowVisible);
  }, [processedRecords, isRowVisible]);

  const handleExpandAll = useCallback(() => {
    setCollapsedIds([]);
  }, []);

  const handleCollapseAll = useCallback(() => {
    const parentIds = processedRecords.filter((r) => r._hasChildren).map((r) => r._id);
    setCollapsedIds(parentIds);
  }, [processedRecords]);

  const handleBulkEdit = useCallback((e) => {
    e.preventDefault();
    if (selectedRowsId.length > 50) {
      setIsNotification(true);
      setNotificationMsg("Maximum 50 categories can be bulk edited at a time.");
      setSeverity("error");
      return;
    }
    setOpenBulkEditModal(true);
  }, [selectedRowsId.length]);

  const handleAddNewCategory = useCallback(() => {
    const prevUrl = Page > 1
      ? `/blog/categories?page=${Page}${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''}`
      : `/blog/categories${debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : ''}`;
    parentNavigate("/blog/categories/add", {
      prevPage: Page,
      prevUrl,
    });
  }, [Page, debouncedSearch]);

  const handleEditCategory = useCallback((row) => {
    if (!row?._id) return;
    const prevUrl = Page > 1
      ? `/blog/categories?page=${Page}${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''}`
      : `/blog/categories${debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : ''}`;

    parentNavigate(`/blog/categories/${row._id}`, {
      prevPage: Page,
      prevUrl,
    });
  }, [Page, debouncedSearch]);

  // Optimistic Single & Bulk Delete
  const HandleDelete = useCallback(async () => {
    const targetIds = [...selectedRowsId];
    if (targetIds.length === 0) return;

    setConfirmationPopup(false);

    // Snapshot current state for rollback
    const previousCategoryData = BlogCategory;
    const previousQueries = queryClient.getQueriesData({ queryKey: ["blogCategories"] });

    // 1. Optimistic local state update
    const currentRecords = BlogCategory?.data?.records || [];
    const updatedRecords = currentRecords.filter((item) => !targetIds.includes(item?._id));
    const newTotal = Math.max(0, categoryTotalRecords - targetIds.length);
    const numLimit = Number(limit);
    const newTotalPages = Number.isFinite(numLimit) && numLimit > 0
      ? Math.ceil(newTotal / numLimit) || 1
      : 1;

    setBlogCategoryState(updatedRecords, newTotal, newTotalPages);

    // 2. Cascade shift items from next cached pages
    const matchingQueries = queryClient.getQueriesData({ queryKey: ["blogCategories"] });
    if (Number.isFinite(numLimit) && numLimit > 0) {
      const pageMap = new Map();
      matchingQueries.forEach(([key, qData]) => {
        const pageNum = Number(key[1]) || 1;
        const recs = [...(qData?.data?.records || qData?.records || [])].filter(
          (item) => !targetIds.includes(item?._id)
        );
        pageMap.set(pageNum, { key, qData, recs });
      });

      const sortedPageNums = Array.from(pageMap.keys()).sort((a, b) => a - b);
      const startIndex = sortedPageNums.indexOf(Page);

      if (startIndex !== -1) {
        for (let i = startIndex; i < sortedPageNums.length; i++) {
          const currP = sortedPageNums[i];
          const currEntry = pageMap.get(currP);

          for (let j = i + 1; j < sortedPageNums.length; j++) {
            const nextP = sortedPageNums[j];
            const nextEntry = pageMap.get(nextP);
            const needed = numLimit - currEntry.recs.length;
            if (needed <= 0) break;

            const toShift = nextEntry.recs.splice(0, needed);
            currEntry.recs.push(...toShift);
          }
        }
      }

      pageMap.forEach(({ key, qData, recs }) => {
        const updated = {
          ...qData,
          ...(qData?.data
            ? {
              data: {
                ...qData.data,
                records: recs,
                totalRecords: newTotal,
                totalPages: newTotalPages,
              },
            }
            : {
              records: recs,
              totalRecords: newTotal,
              totalPages: newTotalPages,
            }),
        };
        queryClient.setQueryData(key, updated);
        if (Number(key[1]) === Page) {
          setAllBlogCategory(updated);
        }
      });
    } else {
      queryClient.setQueriesData({ queryKey: ["blogCategories"] }, (oldData) => {
        if (!oldData) return oldData;
        const recs = (oldData?.data?.records || oldData?.records || []).filter(
          (item) => !targetIds.includes(item?._id)
        );
        if (oldData?.data) {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              records: recs,
              totalRecords: newTotal,
              totalPages: newTotalPages,
            },
          };
        }
        return {
          ...oldData,
          records: recs,
          totalRecords: newTotal,
          totalPages: newTotalPages,
        };
      });
    }

    // Adjust page if empty
    if (updatedRecords.length === 0 && Page > 1) {
      setPage((prev) => Math.max(1, prev - 1));
    }

    setSelectedRowsId([]);
    setIsNotification(true);
    setNotificationMsg(
      targetIds.length > 1
        ? `${targetIds.length} blog categories deleted successfully.`
        : "Blog category removed successfully."
    );
    setSeverity("success");

    // 3. Background API call
    try {
      await deleteBlogCategories(targetIds);
    } catch (error) {
      console.error(error, "error----delete blog category");
      // Rollback on error
      if (previousCategoryData) setAllBlogCategory(previousCategoryData);
      previousQueries.forEach(([key, val]) => queryClient.setQueryData(key, val));

      setIsNotification(true);
      setNotificationMsg(error?.message || error || "Failed to delete blog category.");
      setSeverity("error");
    }
  }, [selectedRowsId, BlogCategory, categoryTotalRecords, limit, Page, queryClient]);

  function setBlogCategoryState(recs, total, totalP) {
    setAllBlogCategory((prev) => {
      if (!prev) return prev;
      if (prev?.data) {
        return {
          ...prev,
          data: {
            ...prev.data,
            records: recs,
            totalRecords: total,
            totalPages: totalP,
          },
        };
      }
      return {
        ...prev,
        records: recs,
        totalRecords: total,
        totalPages: totalP,
      };
    });
  }

  const handleCloseConfirm = useCallback((value) => {
    setConfirmationPopup(value);
  }, []);

  const handleClose = useCallback(() => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  }, []);

  const handleSelectRow = useCallback((e, id) => {
    if (e?.nativeEvent?.shiftKey) {
      const prevIndex = visibleRecords?.findIndex(
        (x) => x._id === selectedRowsId[selectedRowsId?.length - 1],
      );
      const currIndex = visibleRecords?.findIndex((x) => x._id === id);
      const startIndex = Math.min(prevIndex, currIndex);
      const endIndex = Math.max(prevIndex, currIndex);
      const shiftSelected = visibleRecords?.slice(startIndex, endIndex + 1);

      if (selectedRowsId?.includes(id)) {
        setSelectedRowsId((prev) => [
          ...new Set(prev.filter((x) => !shiftSelected.find((item) => item._id === x))),
        ]);
      } else {
        setSelectedRowsId((prev) => [
          ...new Set([...prev, ...shiftSelected.map((item) => (!item.default ? item._id : ""))]),
        ]);
      }
    } else if (id === "all") {
      if (selectedRowsId?.length > 0) {
        setSelectedRowsId([]);
      } else {
        setSelectedRowsId(visibleRecords?.map((x) => (!x?.default ? x?._id : "")));
      }
    } else {
      const existId = selectedRowsId?.includes(id);
      if (!existId) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((x) => x !== id));
      }
    }
  }, [visibleRecords, selectedRowsId]);

  const resetFilter = useCallback((e) => {
    e.preventDefault();
    setFilter(false);
    setAnchorEl(null);
    setSearch("");
    setPage(1);
    setLimit(limit === "all" ? 10 : limit);
  }, [limit]);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.blogcategory)) return dataObj.blogcategory;
    if (Array.isArray(tableColumnsData?.blogcategory)) return tableColumnsData.blogcategory;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'parent_category', 'description', 'blog_count', 'action', 'actions',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' › ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: '160px',
          minWidth: '110px',
          responsiveWidth: '100px',
          headerClassName: 'px-3 whitespace-nowrap truncate',
          cellClassName: 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === '') return '-';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            if (
              typeof val === 'string' &&
              (fieldName.toLowerCase().includes('date') ||
                fieldName.toLowerCase().includes('createdat') ||
                fieldName.toLowerCase().includes('updatedat') ||
                fieldName.toLowerCase().endsWith('at') ||
                /^\d{4}-\d{2}-\d{2}T/.test(val))
            ) {
              return formatDate(val);
            }
            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
          },
        };
      });
  }, [apiFieldNames]);

  const columns = useMemo(() => {
    return [
      {
        key: "checkbox",
        title: (
          <Checkbox
            checked={
              visibleRecords?.length > 0 &&
              selectedRowsId?.length === visibleRecords?.length
            }
            indeterminate={
              selectedRowsId?.length > 0 &&
              selectedRowsId?.length < visibleRecords?.length
            }
            onChange={(e) => handleSelectRow(e, "all")}
          />
        ),
        align: "text-center",
        ignoreRowClick: true,
        width: "50px",
        minWidth: "50px",
        maxWidth: "50px",
        headerClassName: "!px-0 text-center",
        cellClassName: "!px-0 text-center",
        render: (row) => (
          <Checkbox
            disabled={row?.default}
            checked={selectedRowsId?.length > 0 && selectedRowsId?.includes(row._id)}
            onChange={(e) => handleSelectRow(e, row._id)}
          />
        ),
      },
      {
        key: "parent_category",
        title: t("lang_Blog_Category") || "Blog Category",
        minWidth: "260px",
        headerClassName: "px-3 text-left",
        cellClassName: "px-3 text-left",
        render: (row) => {
          const level = row._treeLevel || 0;
          const hasChildren = row._hasChildren;
          const isCollapsed = collapsedIds.includes(row._id);

          return (
            <div
              className="flex items-center gap-1.5 py-1 select-none"
              style={{ paddingLeft: `${level * 24}px` }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapse(row._id);
                  }}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-500/20 text-[var(--primary-text-color)] opacity-80 hover:opacity-100 transition-colors shrink-0"
                  title={isCollapsed ? "Expand subcategories" : "Collapse subcategories"}
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-[var(--primary-text-color)]" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[var(--primary-text-color)]" />
                  )}
                </button>
              ) : level > 0 ? (
                <span className="w-5 text-[var(--table-text-color)] opacity-60 font-mono text-xs text-center shrink-0 select-none me-1">
                  └─
                </span>
              ) : (
                <span className="w-5 shrink-0" />
              )}

              <div className="flex flex-col min-w-0">
                <span
                  className="capitalize font-medium text-sm truncate text-[var(--primary-text-color)]"
                  title={row.parent_category || row._treeDisplayName}
                >
                  {row._treeDisplayName}
                </span>
                {level > 0 && (
                  <span
                    className="text-xs text-[var(--table-text-color)] opacity-70 font-normal truncate max-w-[280px]"
                    title={row.parent_category}
                  >
                    {row.parent_category}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: "description",
        title: t("lang_description") || "Description",
        minWidth: "220px",
        headerClassName: "px-3 text-left",
        cellClassName: "px-3 text-left",
        render: (row) => (
          <span className="capitalize line-clamp-2 text-xs text-[var(--table-text-color)]">
            {row?.description ? row.description.replace(/<[^>]*>/g, " ") : "-"}
          </span>
        ),
      },
      {
        key: "blog_count",
        title: t("lang_Blog_Count") || "Blog Count",
        width: "100px",
        maxWidth: "120px",
        headerClassName: "px-3",
        cellClassName: "px-3",
        render: (row) => (
          <span className="text-xs font-semibold text-[var(--primary-text-color)]">
            {row?.blog_count ?? 0}
          </span>
        ),
      },
      ...dynamicColumns,
      {
        key: "action",
        title: t("lang_action") || "Action",
        width: "80px",
        minWidth: "80px",
        maxWidth: "80px",
        headerClassName: "px-3 text-right",
        cellClassName: "px-3 text-right",
        align: "text-right",
        ignoreRowClick: true,
        render: (row) => (
          <div className="flex items-center w-full justify-end space-x-2">
            <Tooltip title="Edit" arrow>
              <button
                onClick={() => handleEditCategory(row)}
                className="text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors"
              >
                <Edit className="w-[16px] h-[16px]" />
              </button>
            </Tooltip>
            {!row?.default && (
              <Tooltip title="Delete" arrow>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedRowsId([row?._id]);
                    setConfirmationPopup(true);
                  }}
                  className="text-red-500 hover:text-red-600 transition-colors"
                >
                  <Trash className="w-[16px] h-[16px]" />
                </button>
              </Tooltip>
            )}
          </div>
        ),
      },
    ];
  }, [
    visibleRecords,
    selectedRowsId,
    handleSelectRow,
    collapsedIds,
    toggleCollapse,
    dynamicColumns,
    handleEditCategory,
  ]);

  const tableHeaderFilter = useCallback((manageFieldsBtn) => {
    return (dataLoading || categoryTotalRecords > 0 || BlogCategory?.data?.records?.length > 0 || search) ? (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-end w-full gap-2 min-h-[38px]">
          <div className="flex items-center gap-2 ml-auto hidden md:flex">
            {processedRecords.some((r) => r._hasChildren) && (
              <div className="flex items-center gap-1.5 me-1">
                <ButtonComponent
                  variant="outline"
                  size="xs"
                  className="!text-xs !py-1 !px-2"
                  onClick={handleExpandAll}
                >
                  Expand All
                </ButtonComponent>
                <ButtonComponent
                  variant="outline"
                  size="xs"
                  className="!text-xs !py-1 !px-2"
                  onClick={handleCollapseAll}
                >
                  Collapse All
                </ButtonComponent>
              </div>
            )}

            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search Blog Categories"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>
            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={categoryTotalRecords}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <div className="flex items-center gap-2 md:hidden ml-auto">
            {processedRecords.some((r) => r._hasChildren) && (
              <div className="flex items-center gap-1.5">
                <ButtonComponent
                  variant="outline"
                  size="xs"
                  className="!text-xs !py-1 !px-2"
                  onClick={handleExpandAll}
                >
                  Expand All
                </ButtonComponent>
                <ButtonComponent
                  variant="outline"
                  size="xs"
                  className="!text-xs !py-1 !px-2"
                  onClick={handleCollapseAll}
                >
                  Collapse All
                </ButtonComponent>
              </div>
            )}

            <Tooltip title={t("lang_filter") || "Filter"} arrow>
              <button
                type="button"
                onClick={(e) => {
                  setFilter(true);
                  setAnchorEl(e.currentTarget);
                }}
                className="relative flex items-center justify-center p-2 border main-border-color rounded-lg bg-[var(--secondary-bg-color)] hover:bg-gray-50/50 transition-colors w-8 h-8 shrink-0"
              >
                <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
              </button>
            </Tooltip>
          </div>
        </div>

        {categoryTotalRecords > 0 && selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1 w-full">
            <span className="text-sm px-2 whitespace-nowrap">
              {selectedRowsId?.length} Selected
            </span>
            <span className="me-1">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={handleBulkEdit}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
            <ButtonComponent
              variant="danger_outline"
              size="sm"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
              }}
            >
              {t("lang_delete_selection")}
            </ButtonComponent>
          </div>
        )}
      </div>
    ) : null;
  }, [
    dataLoading,
    categoryTotalRecords,
    BlogCategory?.data?.records?.length,
    search,
    processedRecords,
    handleExpandAll,
    handleCollapseAll,
    limit,
    selectedRowsId,
    handleBulkEdit,
  ]);

  return (
    <>
      <ConfirmationPopup
        heading="Delete Blog Category"
        subheading="Do you really want to delete this blog category?"
        confirmationPopup={confirmationPopup}
        handleCloseConfirm={handleCloseConfirm}
        runFunction={HandleDelete}
      />
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow -ml-3.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Blog_Category"), path: "/blog/categories" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Blog_Category")}
              </h4>
            </div>
          </div>
          <div>
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              onClick={handleAddNewCategory}
            >
              {t("lang_Add_Blog_Category")}
            </ButtonComponent>
          </div>
        </div>

        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="blogcategory"
                    columns={columns}
                    data={visibleRecords}
                    tableHeader={tableHeaderFilter}
                    tableFooter={
                      BlogCategory?.data && categoryTotalRecords > 0 ? (
                        <PaginationComponent
                          count={categoryTotalPages}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={categoryTotalRecords}
                          startRecord={startRec}
                          endRecord={endRec}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }
                    loading={dataLoading}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => handleEditCategory(row)}
                    rowClassName={(row) =>
                      `select-none ${selectedRowsId?.find((x) => x === row?._id) ? "selected-row" : ""}`
                    }
                    containerClassName="blogcategory-table sticky-table module-table overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]"
                    allowSortPanel={false}
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultBlogCategoryField}
                    userState={userContext}
                  />
                </div>
              </div>

              <Popover
                open={filter}
                anchorEl={anchorEl}
                onClose={() => {
                  setFilter(false);
                  setAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
                }}
                className="rounded-[6px]"
              >
                <div className="p-[20px] filter secondary-bg-color">
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="flex font-semibold text-sm m-0">
                        <Filter className="me-1 w-4 h-4" />
                        {t("lang_filter")}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => resetFilter(e)}
                        className="text-white bg-gray-800 hover:bg-gray-900 focus:outline-none font-medium rounded-md text-sm px-5 py-2 me-1 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                      >
                        Reset Filter
                      </button>
                    </div>
                  </div>
                  <hr className="main-border-color my-3"></hr>
                  <div className="grid gap-y-5 table-text">
                    <div>
                      <span className="text-sm capitalize font-medium mb-1 inline-block">
                        {t("lang_Search")}
                      </span>
                      <SearchComponent
                        placeholder="Search Blog Categories"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        onClear={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        iconPlacement="left"
                        isClearable={true}
                      />
                    </div>
                    <div>
                      <span className="text-sm table-text font-medium mb-1 inline-block">
                        {t("lang_set_items_limit")}
                      </span>
                      <PageLimitDropDownComponent
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        totalRecords={categoryTotalRecords}
                      />
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown
          onClose={() => setOpenBulkEditModal(false)}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              zIndex: 999999,
              margin: 0,
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              borderRadius: 0,
              backgroundColor: "transparent",
            },
          }}
        >
          <BulkEditBlogCategory
            isBulkEdit={true}
            bulkCategoryIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} categor${count > 1 ? "ies" : "y"} updated successfully!`
              );
              setSeverity("success");
              queryClient.invalidateQueries({ queryKey: ["blogCategories"] });
            }}
          />
        </Dialog>
      )}
    </>
  );
};

export default BlogCategories;
